import Image from "@tiptap/extension-image";

const MIN_WIDTH = 40;
const MIN_BOX_SIZE = 0.01; // ignore drags that normalize to a near-zero rect

/**
 * ResizableImage — extends the default TipTap Image node with a persisted pixel
 * `width` attribute and a corner drag handle (shown only when the editor is
 * editable). Width round-trips through the editor's JSON serialization, so no
 * backend change is needed. Height stays auto, preserving aspect ratio.
 *
 * Also supports `boxes` — a list of normalized (0..1) rectangle annotations
 * `{ x, y, w, h }` drawn over the image (e.g. to call out a UI element), the
 * same idea as the red squares used on workflow screen directions. Boxes
 * persist as a `data-boxes` JSON attribute on the `<img>`, round-tripping
 * through markdown the same way `width` does.
 */
const ResizableImage = Image.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      // Opt-in: when false, the box-annotation *editing* UI (add button, draw
      // mode, per-box remove) is not attached. Existing persisted boxes still
      // render everywhere regardless of this flag.
      allowBoxes: false,
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element) => {
          const attr = element.getAttribute("width");
          if (attr) return parseInt(attr, 10) || null;
          const styleWidth = element.style?.width;
          if (styleWidth && styleWidth.endsWith("px")) {
            return parseInt(styleWidth, 10) || null;
          }
          return null;
        },
        renderHTML: (attributes) => {
          if (!attributes.width) return {};
          return {
            width: attributes.width,
            style: `width: ${attributes.width}px; max-width: 100%; height: auto;`,
          };
        },
      },
      boxes: {
        default: null,
        parseHTML: (element) => {
          const attr = element.getAttribute("data-boxes");
          if (!attr) return null;
          try {
            const parsed = JSON.parse(attr);
            if (!Array.isArray(parsed)) return null;
            const isNum = (n) => typeof n === "number" && Number.isFinite(n);
            const valid = parsed.filter(
              (b) => b && typeof b === "object" && isNum(b.x) && isNum(b.y) && isNum(b.w) && isNum(b.h),
            );
            return valid.length ? valid : null;
          } catch {
            return null;
          }
        },
        renderHTML: (attributes) => {
          if (!Array.isArray(attributes.boxes) || attributes.boxes.length === 0) return {};
          return {
            "data-boxes": JSON.stringify(attributes.boxes),
          };
        },
      },
    };
  },

  addNodeView() {
    const allowBoxes = this.options.allowBoxes;
    return ({ node, editor, getPos }) => {
      let currentNode = node;
      let drawing = false;
      // Editing affordances (add/draw/remove) only when opted-in AND editable;
      // rendering of existing boxes is always on (see renderBoxes).
      const boxEditingEnabled = allowBoxes && editor.isEditable;

      const container = document.createElement("div");
      container.className = "resizable-image";

      const img = document.createElement("img");

      // Push the node's attrs onto the <img>. Called on create AND on every update: ProseMirror
      // reuses an existing node view for a different node of the same type (see `update` below),
      // so `src`/`alt`/`title` must be re-read there too — otherwise the previous document's
      // image stays on screen when the editor's content is replaced in place.
      const applyAttrs = () => {
        const { src, alt, title, width } = currentNode.attrs;
        if (img.getAttribute("src") !== src) img.setAttribute("src", src ?? "");
        if (alt) img.setAttribute("alt", alt);
        else img.removeAttribute("alt");
        if (title) img.setAttribute("title", title);
        else img.removeAttribute("title");
        img.style.width = width ? `${width}px` : "";
      };

      applyAttrs();
      container.appendChild(img);

      // Overlay layer holding the box annotations, sits on top of the image.
      const boxLayer = document.createElement("div");
      boxLayer.className = "image-box-layer";
      container.appendChild(boxLayer);

      const persistBoxes = (nextBoxes) => {
        if (typeof getPos !== "function") return;
        editor
          .chain()
          .command(({ tr }) => {
            tr.setNodeMarkup(getPos(), undefined, {
              ...currentNode.attrs,
              boxes: nextBoxes,
            });
            return true;
          })
          .run();
      };

      const removeBox = (index) => {
        const nextBoxes = (currentNode.attrs.boxes || []).filter((_, i) => i !== index);
        persistBoxes(nextBoxes);
      };

      const renderBoxes = () => {
        boxLayer.innerHTML = "";
        const boxes = currentNode.attrs.boxes || [];
        boxes.forEach((box, index) => {
          const boxEl = document.createElement("div");
          boxEl.className = "image-box";
          boxEl.style.left = `${box.x * 100}%`;
          boxEl.style.top = `${box.y * 100}%`;
          boxEl.style.width = `${box.w * 100}%`;
          boxEl.style.height = `${box.h * 100}%`;

          if (boxEditingEnabled) {
            const remove = document.createElement("span");
            remove.className = "image-box-remove";
            remove.textContent = "×";
            remove.title = "Remove box";
            remove.addEventListener("pointerdown", (e) => {
              e.preventDefault();
              e.stopPropagation();
            });
            remove.addEventListener("click", (e) => {
              e.preventDefault();
              e.stopPropagation();
              removeBox(index);
            });
            boxEl.appendChild(remove);
          }

          boxLayer.appendChild(boxEl);
        });
      };

      const handle = document.createElement("span");
      handle.className = "resize-handle";
      container.appendChild(handle);

      let startX = 0;
      let startWidth = 0;

      const onPointerMove = (e) => {
        // Handle is on the bottom-right corner: dragging right (outward) enlarges.
        const delta = e.clientX - startX;
        const maxWidth = container.parentElement?.clientWidth ?? Infinity;
        let next = Math.round(startWidth + delta);
        next = Math.max(MIN_WIDTH, Math.min(next, maxWidth));
        img.style.width = `${next}px`;
      };

      const onPointerUp = () => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        const finalWidth = parseInt(img.style.width, 10);
        if (typeof getPos === "function" && !Number.isNaN(finalWidth)) {
          editor
            .chain()
            .command(({ tr }) => {
              tr.setNodeMarkup(getPos(), undefined, {
                ...currentNode.attrs,
                width: finalWidth,
              });
              return true;
            })
            .run();
        }
      };

      handle.addEventListener("pointerdown", (e) => {
        if (!editor.isEditable) return;
        e.preventDefault();
        e.stopPropagation();
        startX = e.clientX;
        startWidth = img.offsetWidth;
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
      });

      // --- Box-annotation editing UI (add button + draw mode + per-box
      // remove). Gated behind the opt-in `allowBoxes` option AND edit mode;
      // rendering of existing boxes stays always-on (see renderBoxes).
      if (boxEditingEnabled) {
        const addBoxBtn = document.createElement("span");
        addBoxBtn.className = "image-box-add";
        addBoxBtn.title = "Add box";
        addBoxBtn.textContent = "▢";
        container.appendChild(addBoxBtn);

        const clamp01 = (n) => Math.max(0, Math.min(1, n));

        const setDrawMode = (on) => {
          drawing = on;
          container.classList.toggle("image-box-draw-mode", on);
        };

        // Suppress the press so ProseMirror's node-selection doesn't react,
        // mirroring the resize handle and per-box remove control.
        addBoxBtn.addEventListener("pointerdown", (e) => {
          e.preventDefault();
          e.stopPropagation();
        });

        addBoxBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          setDrawMode(!drawing);
        });

        let drawStartX = 0;
        let drawStartY = 0;
        let drawEl = null;

        const onDrawMove = (e) => {
          if (!drawEl) return;
          const rect = img.getBoundingClientRect();
          const curX = Math.min(Math.max(e.clientX, rect.left), rect.right);
          const curY = Math.min(Math.max(e.clientY, rect.top), rect.bottom);
          const left = Math.min(drawStartX, curX);
          const top = Math.min(drawStartY, curY);
          const width = Math.abs(curX - drawStartX);
          const height = Math.abs(curY - drawStartY);
          drawEl.style.left = `${left - rect.left}px`;
          drawEl.style.top = `${top - rect.top}px`;
          drawEl.style.width = `${width}px`;
          drawEl.style.height = `${height}px`;
        };

        const teardownDraw = () => {
          window.removeEventListener("pointermove", onDrawMove);
          window.removeEventListener("pointerup", onDrawUp);
          window.removeEventListener("pointercancel", onDrawCancel);
          if (drawEl) {
            drawEl.remove();
            drawEl = null;
          }
          setDrawMode(false);
        };

        // If a draw drag is interrupted (touch/stylus pointercancel) instead of
        // ending in pointerup, tear down the listeners and temp element without
        // persisting a box.
        const onDrawCancel = () => {
          teardownDraw();
        };

        const onDrawUp = (e) => {
          const rect = img.getBoundingClientRect();
          teardownDraw();

          if (!rect.width || !rect.height) return;

          const endX = Math.min(Math.max(e.clientX, rect.left), rect.right);
          const endY = Math.min(Math.max(e.clientY, rect.top), rect.bottom);

          const x = clamp01((Math.min(drawStartX, endX) - rect.left) / rect.width);
          const y = clamp01((Math.min(drawStartY, endY) - rect.top) / rect.height);
          const w = clamp01(Math.abs(endX - drawStartX) / rect.width);
          const h = clamp01(Math.abs(endY - drawStartY) / rect.height);

          if (w < MIN_BOX_SIZE || h < MIN_BOX_SIZE) return;

          const nextBoxes = [...(currentNode.attrs.boxes || []), { x, y, w, h }];
          persistBoxes(nextBoxes);
        };

        img.addEventListener("pointerdown", (e) => {
          if (!drawing) return;
          e.preventDefault();
          e.stopPropagation();

          drawStartX = e.clientX;
          drawStartY = e.clientY;

          drawEl = document.createElement("div");
          drawEl.className = "image-box image-box-drawing";
          const rect = img.getBoundingClientRect();
          drawEl.style.left = `${drawStartX - rect.left}px`;
          drawEl.style.top = `${drawStartY - rect.top}px`;
          drawEl.style.width = "0px";
          drawEl.style.height = "0px";
          boxLayer.appendChild(drawEl);

          window.addEventListener("pointermove", onDrawMove);
          window.addEventListener("pointerup", onDrawUp);
          window.addEventListener("pointercancel", onDrawCancel);
        });
      }

      renderBoxes();

      return {
        dom: container,
        update: (updatedNode) => {
          if (updatedNode.type.name !== currentNode.type.name) return false;
          currentNode = updatedNode;
          applyAttrs();
          renderBoxes();
          return true;
        },
      };
    };
  },
});

export default ResizableImage;
